import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useGetCameraTrapById } from "@services/device/cameraTrap/getById";
import Heading from "@commons/Typography/Heading";
import { CircularProgress } from "@mui/material";
import ListPreview from "@commons/Preview/ListPreview";
import Map from "@commons/Map";
import { formatDateTime } from "@utils/formatters";
import Button from "@commons/Button";
import AddIcon from "@mui/icons-material/Add";
import { usePage } from "@hooks/usePage";
import Text from "@commons/Typography/Text";
import AttachmentPreview from "@commons/Preview/AttachmentPreview";
import { useGetCameraTrapAttachments } from "@services/device/cameraTrap/getAttachmentsById";
import { config } from "@/config";
import ModalNewAttachmentsCameraTrap from "./ModalNewAttachmentsCameraTrap";
import ModalEditCameraTrap from "./ModalEditCameraTrap";
import { usePermissions } from "@hooks/usePermissions";

function ViewCameraTrap() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openModal, setDataModal } = usePage();
  const [currentPage, setCurrentPage] = useState(1);
  const { can } = usePermissions();

  const {
    data: attachments,
    isLoading: isLoadingAttachments,
    error: attachmentsError,
  } = useGetCameraTrapAttachments(id ?? "", {
    page: currentPage,
    limit: config.attachmentsGallery.defaultPageSize,
  });

  const {
    data: cameraTrap,
    isLoading,
    error,
  } = useGetCameraTrapById(id ?? null);

  useEffect(() => {
    if (error) {
      toast.error("Erro ao carregar os dados da Câmera Trap.");
      navigate("/devices");
    }
  }, [error, navigate]);

  useEffect(() => {
    if (!isLoading && !cameraTrap && !error) {
      toast.warning("Câmera Trap não encontrada.");
      navigate("/devices");
    }
  }, [isLoading, cameraTrap, error, navigate]);

  if (!cameraTrap) return null;

  const handleAddAttachments = () => {
    setDataModal({ cameraTrapId: cameraTrap.id });
    openModal("addCameraTrapAttachments");
  };

  const previewRows = [
    { label: "Número:", value: cameraTrap.number },
    { label: "Latitude:", value: cameraTrap.latitude },
    { label: "Longitude:", value: cameraTrap.longitude },
    { label: "Equipe Responsável:", value: cameraTrap.responsibleTeam },
    {
      label: "Última Atualização:",
      value: formatDateTime(cameraTrap.updatedAt),
    },
    { label: "Última Edição por:", value: cameraTrap.lastEditor },
  ];

  const canUpdateCameraTrap = can("update", "cameraTraps");

  return (
    <section className="flex flex-col gap-2 items-start w-full bg-white justify-start p-4 sm:p-6 shadow-md rounded-2xl">
      <div className="w-full flex justify-between items-center gap-4 mb-4">
        <Heading>Câmera Trap</Heading>
        <div className="flex gap-2">
          {canUpdateCameraTrap && (
            <Button
              customVariant="muted"
              size="auto"
              onClick={() => {
                setDataModal({ id: cameraTrap.id });
                openModal("editCameraTrap");
              }}
            >
              <Text size="sm">Editar Câmera</Text>
            </Button>
          )}
          <Button
            customVariant="muted"
            size="auto"
            onClick={() => navigate("/devices")}
          >
            <Text size="sm">Voltar</Text>
          </Button>
        </div>
      </div>
      <div className="w-full">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <CircularProgress size={60} className="text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2 items-start w-full h-full justify-start rounded-2xl">
              <ListPreview.Root>
                {previewRows.map((row) => (
                  <ListPreview.Row
                    key={row.label}
                    label={row.label}
                    value={row.value}
                  />
                ))}
              </ListPreview.Root>
            </div>
            <div className="flex flex-col gap-2 items-start w-full h-[36dvh] lg:h-full justify-start rounded-2xl border border-gray-200 overflow-hidden">
              <Map.Localization
                longitude={cameraTrap.longitude}
                latitude={cameraTrap.latitude}
              />
            </div>
            <div className="lg:col-span-2 mt-4">
              <div className="flex justify-between items-start flex-wrap gap-4 mb-4">
                <Heading size="sm">Imagens</Heading>
                {canUpdateCameraTrap && (
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddAttachments}
                    customVariant="secondary"
                    size="auto"
                  >
                    <Text size="sm">Adicionar Anexos</Text>
                  </Button>
                )}
              </div>
              {isLoadingAttachments && (
                <div className="flex justify-center items-center py-4">
                  <CircularProgress size={24} className="text-primary" />
                  <Text size="sm" className="ml-2 text-gray-500">
                    Carregando anexos...
                  </Text>
                </div>
              )}

              {attachmentsError && (
                <div className="flex items-center py-2">
                  <Text size="sm" className="text-red-500">
                    Erro ao carregar anexos. Tente novamente.
                  </Text>
                </div>
              )}

              {!isLoadingAttachments &&
              !attachmentsError &&
              attachments?.items &&
              attachments.items.length > 0 ? (
                <AttachmentPreview
                  attachments={attachments.items}
                  pagination={{
                    pageSize: config.attachmentsGallery.defaultPageSize,
                    currentPage: attachments.meta.currentPage,
                    totalPages: attachments.meta.totalPages,
                    totalItems: attachments.meta.totalItems,
                    onPageChange: setCurrentPage,
                  }}
                />
              ) : (
                <div className="flex justify-center items-center py-8 text-gray-400 bg-gray-50 rounded-md">
                  <Text size="sm">Sem anexos</Text>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <ModalNewAttachmentsCameraTrap />
      <ModalEditCameraTrap />
    </section>
  );
}

export default ViewCameraTrap;
